import { useRef, useCallback, useEffect } from 'react'

interface SwipeConfig {
  /** Callback when user swipes left */
  onSwipeLeft?: () => void
  /** Callback when user swipes right */
  onSwipeRight?: () => void
  /** Minimum distance in pixels to trigger a swipe (default: 75) */
  threshold?: number
  /** Maximum time in milliseconds for a valid swipe (default: 300) */
  allowedTime?: number
}

interface TouchStartData {
  x: number
  y: number
  time: number
}

/**
 * Hook for handling swipe gestures on touch devices
 * Returns a ref to attach to the swipeable container
 *
 * @example
 * const swipeRef = useSwipeGesture({
 *   onSwipeLeft: () => goToNextDay(),
 *   onSwipeRight: () => goToPreviousDay(),
 * })
 * return <div ref={swipeRef}>...</div>
 */
export function useSwipeGesture<T extends HTMLElement = HTMLDivElement>(
  config: SwipeConfig
) {
  const {
    onSwipeLeft,
    onSwipeRight,
    threshold = 75,
    allowedTime = 300,
  } = config

  const touchStart = useRef<TouchStartData | null>(null)
  const containerRef = useRef<T>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0]
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    }
  }, [])

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchStart.current) return

      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - touchStart.current.x
      const deltaY = touch.clientY - touchStart.current.y
      const deltaTime = Date.now() - touchStart.current.time

      // Reset touch start
      touchStart.current = null

      // Check if this was a valid horizontal swipe
      // - Within time limit
      // - Horizontal distance exceeds threshold
      // - Horizontal movement is greater than vertical (to avoid conflicts with scrolling)
      if (
        deltaTime <= allowedTime &&
        Math.abs(deltaX) >= threshold &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        if (deltaX > 0) {
          // Swiped right (finger moved from left to right)
          onSwipeRight?.()
        } else {
          // Swiped left (finger moved from right to left)
          onSwipeLeft?.()
        }
      }
    },
    [onSwipeLeft, onSwipeRight, threshold, allowedTime]
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Use passive listeners for better scroll performance
    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd])

  return containerRef
}
